import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import { CenteredMediaSectionData } from "@/components/Home/v2/CenteredMediaBleedSection";
import { ColoredOutcomeCardsSectionData } from "@/components/Home/v2/ColoredOutcomeCardsSection";
import { ThreePillarCardsSectionData } from "@/components/Product/v2/ThreePillarCardsSection";
import { ScrollAnimatedSplitHeadingSectionData } from "@/components/Product/v2/ScrollAnimatedSplitHeadingSection";
import { ShipFasterWithAccordionData } from "@/components/Product/v2/ShipFasterWithAccordionSection";
import { AlternatingProductsSectionData } from "@/components/Product/v2/AlternatingProductsSection";
import { SplitOutcomeCardsSectionData } from "@/components/Product/v2/SplitOutcomeCardsSection";
import { TemplateShowcaseSectionData } from "@/components/Product/v2/TemplateShowcaseSection";
import { StickyScrollProductsSectionData } from "@/components/Product/v2/StickyScrollProductsSection";
import { DarkMediaChecklistSectionData } from "@/components/Home/v2/DarkMediaChecklistSection";
import { DarkNumberedStepsSectionData } from "@/components/Home/v2/DarkNumberedStepsSection";
import { DarkTwoColCardsSectionData } from "@/components/Home/v2/DarkTwoColCardsSection";
import { IntegrationLogoArcSectionData } from "@/components/Home/v2/IntegrationLogoArcSection";
import {
  BookADemoSourceEnum,
  CtaActionType,
  CtaType,
  SignUpSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { ProductHeroV2Props } from "@/components/Product/v2/ProductHeroV2";
import { GraphyHeadPropType } from "@/components/organisms/GraphyHead";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import {
  AiTeamProps,
  AutomationsProps,
  CtaBannerV2Props,
  FooterV2Props,
  IdeaToIncomeProps,
} from "@/shared/data/home.v2.data";
import { ProductPageSlugEnum } from "@/shared/types/common.types";
import { homeV2Data } from "@/shared/data/home.v2.data";
import { getCDNURL } from "@/shared/utils/commons";
import {
  faBookOpen,
  faBoxOpen,
  faCalendarCheck,
  faUsers,
  faVideo,
  faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";

/**
 * Product V2 data — India (en) locale only.
 *
 * Mirrors the shape of `HomeV2DataType` but for product pages: an SEO
 * block, a hero, a list of AutomationsSection panels (one per major
 * capability), an optional CTA banner, and the shared footer.
 *
 * Product V2 pages are composed entirely out of HomeV2 section components.
 * Only the Courses slug has V2 data today; `[productSlug].tsx` branches
 * on `productV2Data[slug]` so other slugs keep their legacy layout.
 */

/**
 * A single section entry in a product V2 page. Discriminated by `kind` so
 * ProductV2 can map each entry to the correct HomeV2 component at render
 * time. Ordering of the `sections` array is the rendered order.
 */
export type ProductV2Section =
  | { kind: "accordion"; data: AutomationsProps }
  | { kind: "card-grid"; data: IdeaToIncomeProps }
  | { kind: "ai-team"; data: AiTeamProps }
  | { kind: "stories"; data: ProductV2StoriesSection }
  | { kind: "faq"; data: ProductV2FaqSection }
  | { kind: "centered-media"; data: CenteredMediaSectionData }
  | { kind: "dark-two-col-cards"; data: DarkTwoColCardsSectionData }
  | { kind: "dark-numbered-steps"; data: DarkNumberedStepsSectionData }
  | { kind: "dark-media-checklist"; data: DarkMediaChecklistSectionData }
  | { kind: "integration-logo-arc"; data: IntegrationLogoArcSectionData }
  | { kind: "colored-outcome-cards"; data: ColoredOutcomeCardsSectionData }
  | { kind: "three-pillar-cards"; data: ThreePillarCardsSectionData }
  | { kind: "scroll-animated-heading"; data: ScrollAnimatedSplitHeadingSectionData }
  | { kind: "ship-faster-with-accordion"; data: ShipFasterWithAccordionData }
  | { kind: "alternating-products"; data: AlternatingProductsSectionData }
  | { kind: "sticky-scroll-products"; data: StickyScrollProductsSectionData }
  | { kind: "split-outcome-cards"; data: SplitOutcomeCardsSectionData }
  | { kind: "template-showcase"; data: TemplateShowcaseSectionData };

// Re-export the new section data types so consumers (e.g. feature.v2.data.ts)
// can import them alongside the existing section types.
export type {
  ShipFasterWithAccordionData,
  CenteredMediaSectionData,
  DarkTwoColCardsSectionData,
  DarkNumberedStepsSectionData,
  DarkMediaChecklistSectionData,
  IntegrationLogoArcSectionData,
  ColoredOutcomeCardsSectionData,
  ThreePillarCardsSectionData,
  ScrollAnimatedSplitHeadingSectionData,
  AlternatingProductsSectionData,
  StickyScrollProductsSectionData,
  SplitOutcomeCardsSectionData,
  TemplateShowcaseSectionData,
};

export type ProductV2StoryCard = {
  title: string;
  quote: string;
  name: string;
  role: string;
  avatar?: string;
};

export type ProductV2StoriesSection = {
  eyebrow?: string;
  titleMuted: string;
  titleBright: string;
  subtitle?: string;
  ctas?: CtaType[];
  stories: ProductV2StoryCard[];
};

export type ProductV2FaqItem = {
  question: string;
  answer: string;
};

export type ProductV2FaqSection = {
  title: string;
  ctas?: CtaType[];
  items: ProductV2FaqItem[];
};

export type ProductV2DataType = {
  seoData: GraphyHeadPropType;
  hero: ProductHeroV2Props;
  /** Heterogeneous ordered list of sections. */
  sections: ProductV2Section[];
  ctaBanner: CtaBannerV2Props;
  footer: FooterV2Props;
};

// Single shared testimonial set used by every product V2 page. Avatars
// resolve from the LMS folder (same assets ship to every product folder
// on the CDN, so we pick one canonical location rather than duplicating
// per-page paths). Section headings remain per-page — only the creator
// cards are shared.
// Shared across product / LMS / alternative pages — see cdn-v4 dedupe report.
const TESTIMONIAL_AVATAR_BASE = `${getCDNURL()}graphy-web/V4/images/Testimonial Images`;

export const sharedProductStories: ProductV2StoryCard[] = [
  {
    title: "Reliable Partner",
    quote:
      "This is a human business. You don't choose a platform because it's cheap or fancy. You choose people you can depend on. And Graphy has been that partner for me.",
    name: "Atish Mathur",
    role: "Educator",
    avatar: `${TESTIMONIAL_AVATAR_BASE}/Athish Mathur.png`,
  },
  {
    title: "Selling Made Simple",
    quote:
      "Graphy has made my life easier. I can finally focus on what I do best — teaching — while the platform takes care of everything else.",
    name: "Dr. Anand Mani",
    role: "Coach",
    avatar: `${TESTIMONIAL_AVATAR_BASE}/Dr. Anand Mani.png`,
  },
  {
    title: "Sell Worldwide, Get Paid",
    quote:
      "What truly sets Graphy apart for us is its stability. We've experienced virtually zero downtime since launch, which gives us immense confidence, especially during high-stakes live sessions.",
    name: "Dnyanadeep Academy",
    role: "Educator",
    avatar: `${TESTIMONIAL_AVATAR_BASE}/Dnyanadeep Academy.png`,
  },
  {
    title: "Easy To Manage",
    quote:
      "Graphy makes managing our UPSC courses seamless. Live classes, recorded lectures, and current affairs updates in one place keep students engaged, motivated, and on track with their preparation every day.",
    name: "Aman Sharma",
    role: "Founder | StudyIAS",
    avatar: `${TESTIMONIAL_AVATAR_BASE}/Aman Sharma.png`,
  },
  {
    title: "My Go-To Platform",
    quote:
      "Webveda, a 5 lakh+ student community is run by 1 full-time employee and 8 interns only. This is made possible only by Graphy. Right from conducting courses, assignments, live sessions to progress tracking, Graphy handles everything for us.",
    name: "Ankur Warikoo",
    role: "Coach",
    avatar: `${TESTIMONIAL_AVATAR_BASE}/Ankur Warikoo.png`,
  },
  {
    title: "Everything In One Place",
    quote:
      "With Graphy, we've scaled our programs to reach aspiring professionals across countries — all from one platform. The ease of managing course content, payments, and learner communication has allowed us to focus more on mentorship and less on operations.",
    name: "Aarushi Bedi",
    role: "Founder | The Fluent Flyers",
    avatar: `${TESTIMONIAL_AVATAR_BASE}/Aarushi Bedi.png`,
  },
];

export const heroPrimaryCta: CtaType = {
  type: ButtonTypeEnum.Secondary,
  size: ButtonSizeEnum.Regular,
  text: "Start Your Free Trial",
  actionType: CtaActionType.SignUp,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
    eventProperties: {
      source: SignUpSourceEnum.hero,
    },
  },
};

export const heroSecondaryCta: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Book A Demo",
  actionType: CtaActionType.BookDemo,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
    eventProperties: {
      source: BookADemoSourceEnum.hero,
    },
  },
};

const coursesSeo: GraphyHeadPropType = {
  title: "Create & Sell Online Courses",
  description:
    "Trusted platform to create, market, and sell online courses with integrated payments. Start creating and selling courses with AI for free.",
  canonicalPath: "create-and-sell-course-online",
  countryCode: "",
};

const coursesCreateSection: AutomationsProps = {
  eyebrow: "CREATE",
  title: "Create & Sell Courses\nFaster and Without Friction",
  subtitle:
    "Turn your knowledge into structured, engaging courses. Start with an idea. Graphy helps you turn it into a professional course experience without technical complexity.",
  features: [
    {
      id: "choose-course-format",
      title: "Choose Your Course Format",
      description:
        "Start with how you want to teach. Design courses that fit your teaching style and audience — not rigid templates.",
      bullets: [
        {
          icon: "clipboard",
          text: "Self-paced, cohort-based, hybrid, or live-supported courses",
        },
        {
          icon: "flag",
          text: "Set learning outcomes, duration, and structure upfront",
        },
        {
          icon: "window",
          text: "Organize everything in one clean curriculum view",
        },
        {
          icon: "graduation",
          text: "Build multi-format learning paths with modules & milestones",
        },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Create Section/Choose Your Course Format.png`,
    },
    {
      id: "create-content-with-ai",
      title: "Create Content With AI",
      description:
        "Graphy's AI helps you go from idea to lessons in minutes, using your voice and knowledge.",
      bulletsHeading: "How It Works:",
      bullets: [
        { icon: "lightbulb", text: "Generate course description in one click" },
        { icon: "flask", text: "Turn a single prompt into modules, lessons & topic breakdowns" },
        { icon: "envelope", text: "Auto-generate course landing pages" },
        { icon: "share", text: "Easily edit as per your needs after generation" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Create Section/Create Content With AI.png`,
    },
    {
      id: "build-rich-learning",
      title: "Build Rich Learning Experiences",
      description:
        "Create lessons that feel valuable, engaging, and worth paying for.",
      bulletsHeading: "What It Handles:",
      bullets: [
        { icon: "question", text: "Add videos, PDFs, quizzes, assignments & downloads" },
        { icon: "chart", text: "Enable your AI Avatar on courses for easy access to your knowledge and practical doubt solving" },
        { icon: "key", text: "Enable discussions, comments & peer learning inside lessons" },
        { icon: "headset", text: "Add completion certificates and progress tracking" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Create Section/Build Rich Learning Experiences.png`,
    },
    {
      id: "launch-instantly",
      title: "Launch Instantly, No Developer Needed",
      description:
        "Track every visitor, every sale, every student action. See which marketing works, which courses sell best, and where to focus your energy.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "window", text: "Auto-generate your course sales page with AI" },
        { icon: "tag", text: "Upload content and publish instantly" },
        { icon: "globe", text: "Preview your course on mobile & desktop" },
        { icon: "moon", text: "Enable payments, access rules & enrolment settings" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Create Section/Launch Instantly, No Developer Needed.png`,
    },
  ],
};

const coursesSellSection: AutomationsProps = {
  eyebrow: "SELL",
  title: "Sell & Market\nFrom One Unified Dashboard",
  subtitle:
    "Turn visitors into buyers with pages designed for clarity, trust, and action — all managed from a single dashboard.",
  // Flip the visual rhythm on alternating sections — Create has image on
  // the right, Sell has image on the left, so the eye zig-zags down the
  // page instead of marching in one direction.
  imagePosition: "left",
  features: [
    {
      id: "conversion-landing-pages",
      title: "Create Conversion Ready Landing Pages",
      description:
        "Turn visitors into buyers with pages designed for clarity, trust, and action — no design skills required.",
      bullets: [
        {
          icon: "window",
          text: "Conversion-optimized sales pages made ready by AI Website builder",
        },
        { icon: "lightbulb", text: "AI-generated copy, layouts & CTAs" },
        {
          icon: "share",
          text: "Mobile-responsive designs ready to publish",
        },
        {
          icon: "question",
          text: "Add reviews, proof, FAQs & bonuses easily",
        },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Sell Section/Create Conversion Ready Landing Pages.png`,
    },
    {
      id: "accept-payments",
      title: "Accept Payments Seamlessly",
      description:
        "Make buying effortless with secure payments, global gateways, and flexible pricing options.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "tag", text: "Built-in checkout with one-click purchase" },
        { icon: "globe", text: "Support for global cards, UPI, wallets & more" },
        { icon: "receipt", text: "One-time, subscription, EMI & payment plans" },
        { icon: "moon", text: "Automated invoices, tax handling & receipts" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Sell Section/Accept Payments Seamlessly.png`,
    },
    {
      id: "capture-leads",
      title: "Capture Leads & Automate Marketing",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "envelope", text: "Lead capture forms and funnel-ready pages" },
        { icon: "share", text: "Email broadcasts & drip campaigns" },
        { icon: "window", text: "Automated follow-ups for abandoned carts" },
        { icon: "flask", text: "Push notifications on mobile apps" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Sell Section/Capture Leads & Automate Marketing.png`,
    },
    {
      id: "track-sales",
      title: "Track Sales, Revenue & Engagement",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "chart", text: "Revenue by product & source" },
        { icon: "graduation", text: "Engagement and completion tracking" },
        { icon: "flag", text: "Marketing campaign performance" },
        { icon: "lightbulb", text: "Learner performance report" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Sell Section/Track Sales, Revenue & Engagement.png`,
    },
  ],
};

const launchCtas: CtaType[] = [heroPrimaryCta, heroSecondaryCta];

const coursesLaunchSection: IdeaToIncomeProps = {
  eyebrow: "LAUNCH",
  title: "Sell Globally. Payments Built for Modern Creators.",
  subtitle:
    "Complex payments kill conversions. Graphy gives you flexible pricing, global and local gateways, and automated taxes and invoices.",
  ctas: launchCtas,
  columns: 3,
  cards: [
    {
      title: "Flexible Pricing Models",
      description:
        "Sell one-time courses, subscriptions, bundles, or payment plans — all from one place.",
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Launch Section/Flexible Pricing Models.png`,
    },
    {
      title: "Sell Worldwide, Get Paid Locally",
      description:
        "Accept payments in 120+ currencies with global and local gateways.",
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Launch Section/Sell Worldwide, Get Paid Locally.png`,
    },
    {
      title: "Taxes and Invoices, Automated",
      description:
        "Taxes and invoices are handled automatically — no spreadsheets needed.",
      image: `${getCDNURL()}graphy-web/V4/images/Courses Page/Launch Section/Taxes and Invoices, Automated.png`,
    },
  ],
};

// Reuse the homepage Scale panel verbatim — same copy, same cards, just
// rendered on product pages too.
const coursesScaleSection: AiTeamProps = homeV2Data.aiTeam;

export const coursesStoriesSection: ProductV2StoriesSection = {
  eyebrow: "STORIES",
  titleMuted: "Creators Who Sell More,",
  titleBright: "Without Doing More",
  subtitle:
    "Thousands of creators use Graphy to automate selling, deliver better experiences, and grow their course businesses globally.",
  ctas: launchCtas,
  stories: sharedProductStories,
};

const coursesFaqSection: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: launchCtas,
  items: [
    {
      question: "Can I offer certificates of completion for my Graphy courses?",
      answer:
        "Absolutely! You can reward your students with custom certificates upon course completion when you sell courses online with Graphy. Certificates add value to your courses and motivate learners to succeed.",
    },
    {
      question:
        "Is there a limit to the number of courses I can create with the Graphy Course Creator?",
      answer: "No, you can create unlimited online courses on Graphy.",
    },
    {
      question:
        "Can I offer discounts or promotions for my Graphy courses when I sell courses online?",
      answer:
        "Yes, Graphy provides the flexibility to offer discounts or run promotions for your courses. You can attract more students, boost enrollment, and tailor your pricing.",
    },
    {
      question: "How can I start to sell courses online with Graphy?",
      answer:
        "To sell courses online through Graphy, simply build your course, publish it on our platform, and then promote it to reach your target audience. Our user-friendly tools make it easy to share your expertise and generate revenue by selling courses online.",
    },
    {
      question: "Can I have multiple instructors within one course?",
      answer:
        "Yes, Graphy supports multiple instructors to be a part of the course. It also offers the flexibility to switch between self-paced and instructor-led courses as you see fit when selling courses online. Adapt your teaching style to cater to a wide range of learner preferences.",
    },
    {
      question:
        "Can I customize my course page to match my branding while selling courses online?",
      answer:
        "Absolutely! You can personalize your course page on Graphy to reflect your unique branding and style. It's a great way to create a professional and memorable online presence while selling courses online.",
    },
  ],
};

// ---------------------------------------------------------------------------
// WEBINAR
// ---------------------------------------------------------------------------

const webinarSeo: GraphyHeadPropType = {
  title: "Host & Sell Webinars → Graphy",
  description:
    "Host live webinars with registrations, payments, and engagement tools built in. Launch high-converting webinars easily with Graphy.",
  canonicalPath: "conduct-live-webinars",
  countryCode: "",
};

const webinarCreateSection: AutomationsProps = {
  eyebrow: "CREATE",
  title: "Host Live Webinars\nThat Drive Real Engagement",
  subtitle:
    "Set up immersive live sessions in minutes. Run Q&A, polls, and interactive workshops that turn viewers into loyal learners.",
  features: [
    {
      id: "choose-format",
      title: "Choose Your Webinar Format",
      description:
        "Decide how you want to host your sessions. Build webinars that match your goals.",
      bullets: [
        { icon: "headset", text: "Live webinars, workshops, masterclasses & AMAs" },
        { icon: "chart", text: "Free, paid, or lead-magnet webinars" },
        { icon: "clipboard", text: "One time, limited time or lifelong access" },
        { icon: "window", text: "Create registration, thank-you & replay pages" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section/Choose Your Webinar Format.png`,
    },
    {
      id: "interactive-sessions",
      title: "Create High-Value, Interactive Sessions",
      description:
        "Deliver polished, professional webinars that keep attendees engaged from start to finish.",
      bullets: [
        { icon: "question", text: "Add chat, polls & interactive tools" },
        { icon: "share", text: "Upload slides, PDFs & downloadable resources" },
        { icon: "flask", text: "Design structured session flows" },
        { icon: "key", text: "Add replay pages with gated or free access" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section/Create High-Value, Interactive Sessions.png`,
    },
    {
      id: "post-webinar-funnels",
      title: "Build Post-Webinar Funnels That Convert",
      description:
        "Turn every session into revenue opportunities with follow-ups, offers, and automated nurturing.",
      bullets: [
        { icon: "chart", text: "Create upsells & post-session offers" },
        { icon: "envelope", text: "Build follow-up sequences & thank-you flows" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section/Build Post-Webinar Funnels That Convert.png`,
    },
    {
      id: "launch-in-minutes",
      title: "Launch Your Webinar In Minutes",
      description:
        "Go from topic to live session quickly. Everything is ready out-of-the-box — mobile-friendly, optimized, and effortless.",
      bullets: [
        { icon: "lightbulb", text: "Auto-generate your webinar landing page" },
        { icon: "envelope", text: "Activate automated reminders & confirmations" },
        { icon: "chart", text: "Enable payments instantly" },
        { icon: "flag", text: "Launch registration & replay access in one click" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section/Launch Your Webinar In Minutes.png`,
    },
  ],
};

const webinarLaunchSection: IdeaToIncomeProps = {
  eyebrow: "LAUNCH",
  title: "Ticket Sales, Reminders & Recordings — All Handled",
  subtitle:
    "Schedule, sell, and deliver your webinars end-to-end. Graphy handles the plumbing so you can focus on the session itself.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  columns: 3,
  cards: [
    {
      title: "Sell Paid Webinars",
      description:
        "Charge for access with flexible pricing. Free webinars work too — toggle in one click.",
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Launch Section/Flexible Pricing Models.png`,
    },
    {
      title: "Automated Reminders",
      description:
        "We send email and in-app reminders so your attendees show up — without you lifting a finger.",
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Launch Section/Sell Worldwide, Get Paid Locally.png`,
    },
    {
      title: "Recordings On Tap",
      description:
        "Send recordings to registrants automatically. Turn every live session into evergreen content.",
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Launch Section/Taxes and Invoices, Automated.png`,
    },
  ],
};

const webinarSellSection: AutomationsProps = {
  eyebrow: "SELL",
  title: "Monetize Every Live Session\nFrom One Unified Dashboard",
  subtitle:
    "Sell tickets, manage attendees, and track revenue across every webinar — from a single dashboard built for creators.",
  imagePosition: "left",
  features: [
    {
      id: "webinar-sell-pages",
      title: "Conversion-Ready Registration Pages",
      description:
        "Publish beautiful webinar landing pages in minutes — with speaker bios, agenda, and social proof.",
      bullets: [
        { icon: "window", text: "AI-generated registration page layouts" },
        { icon: "tag", text: "One-time, series, and bundle pricing" },
        { icon: "share", text: "Shareable invite links and social assets" },
        { icon: "question", text: "Add FAQs and speaker bios" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section 02/Create Conversion Ready Landing Pages.png`,
    },
    {
      id: "webinar-payments",
      title: "Accept Payments Seamlessly",
      description:
        "Make buying effortless with secure payments, global gateways, and flexible pricing options.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "tag", text: "Built-in checkout with one-click purchase" },
        { icon: "globe", text: "Support for global cards, UPI, wallets & more" },
        { icon: "receipt", text: "One-time, subscription, EMI & payment plans" },
        { icon: "moon", text: "Automated invoices, tax handling & receipts" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section 02/Accept Payments Seamlessly.png`,
    },
    {
      id: "webinar-marketing",
      title: "Capture Leads & Automate Marketing",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "envelope", text: "Lead capture forms and funnel-ready pages" },
        { icon: "share", text: "Email broadcasts & drip campaigns" },
        { icon: "flask", text: "Automated follow-ups for abandoned carts" },
        { icon: "lightbulb", text: "Push notifications on mobile apps" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section 02/Capture Leads & Automate Marketing.png`,
    },
    {
      id: "webinar-insights",
      title: "Track Sales, Revenue & Engagement",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "chart", text: "Revenue by product & source" },
        { icon: "graduation", text: "Engagement and completion tracking" },
        { icon: "flag", text: "Marketing campaign performance" },
        { icon: "lightbulb", text: "Learner performance report" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Sell Section 02/Track Sales, Revenue & Engagement.png`,
    },
  ],
};

const webinarScaleSection: AiTeamProps = homeV2Data.aiTeam;

const webinarStoriesSection: ProductV2StoriesSection = {
  eyebrow: "STORIES",
  titleMuted: "Creators Running Webinars",
  titleBright: "That Actually Convert",
  subtitle:
    "From free community sessions to premium workshops, Graphy helps creators host, sell, and scale webinars that drive real revenue.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  stories: sharedProductStories,
};

const webinarFaqSection: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question: "Can I sell paid webinars on Graphy?",
      answer: "Yes — Graphy supports paid webinars with flexible pricing, coupon codes, and secure checkout.",
    },
    {
      question: "Do you provide recordings for webinars as well?",
      answer:
        "Yes, you can automatically share webinar recordings with attendees after the live session ends.",
    },
    {
      question: "Can I add a limit to the number of seats I want to sell?",
      answer:
        "Yes, Graphy supports batch limits so you can cap seats and trigger waitlists automatically.",
    },
    {
      question: "Where are the webinars actually hosted?",
      answer:
        "All webinars run on Graphy Live — our own reliable, low-latency streaming stack built for creators.",
    },
    {
      question: "Is there a limit on the total number of webinars I can create?",
      answer:
        "No, you can host unlimited webinars. You can host one concurrent session by default — upgrade to run more in parallel.",
    },
    {
      question: "Can I run free community webinars alongside paid ones?",
      answer:
        "Absolutely. Mix free sessions for discovery with paid sessions for revenue — all from the same dashboard.",
    },
  ],
};

// ---------------------------------------------------------------------------
// MEMBERSHIP
// ---------------------------------------------------------------------------

const membershipSeo: GraphyHeadPropType = {
  title: "Create, sell, and manage memberships → Graphy",
  description:
    "Create, manage, and scale online memberships with Graphy. Offer exclusive content, engage your community, and earn recurring income.",
  canonicalPath: "launch-membership",
  countryCode: "",
};

const membershipCreateSection: AutomationsProps = {
  eyebrow: "CREATE",
  title: "Create Memberships\nEffortlessly & Without Limits",
  subtitle:
    "Turn your expertise into a value-packed membership. Start with your idea — Graphy helps you shape it into an engaging, recurring-revenue experience without complexity.",
  features: [
    {
      id: "define-model",
      title: "Define Your Membership Model",
      description:
        "Choose how your membership works. Build a structure that fits your content style, community type, and long-term goals—without limitations.",
      bullets: [
        { icon: "clipboard", text: "Build low-ticket, mid-tier, or premium memberships" },
        { icon: "moon", text: "Offer monthly, quarterly or annual access" },
        { icon: "flask", text: "Create one or multiple membership tiers" },
        { icon: "key", text: "Give your members access to exclusive members club" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Create Section/Define Your Membership Model.png`,
    },
    {
      id: "valuable-content",
      title: "Create Value-Packed Member Content",
      description:
        "Deliver content that your members want to return for — consistently. Graphy helps you package, schedule, and deliver high-value content with zero friction.",
      bullets: [
        { icon: "window", text: "Add posts, videos, PDFs & resource drops" },
        { icon: "clipboard", text: "Add weekly/monthly content releases" },
        { icon: "lightbulb", text: "Offer exclusive templates, guides & toolkits" },
        { icon: "share", text: "Share links and other resources" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Create Section/Create Value-Packed Member Content.png`,
    },
    {
      id: "community-spaces",
      title: "Build Engaging Community Spaces",
      description:
        "Build discussions, events, and interactions that increase retention and belonging.",
      bullets: [
        { icon: "headset", text: "Create private channels & topic-based spaces" },
        { icon: "share", text: "Share links to live events, AMAs & workshops" },
        { icon: "flag", text: "Enable member recognition" },
        { icon: "question", text: "Build conversations with comments & reactions" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Create Section/Build Engaging Community Spaces.png`,
    },
    {
      id: "launch-in-minutes",
      title: "Launch Your Membership In Minutes",
      description:
        "Go from idea to publish-ready quickly. Everything works out of the box — mobile-ready, SEO-ready, and designed for conversion.",
      bullets: [
        { icon: "lightbulb", text: "Auto-generate your membership sales page" },
        { icon: "envelope", text: "Publish content & community areas instantly" },
        { icon: "key", text: "Set access rules for each tier" },
        { icon: "window", text: "Preview your membership across devices" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Create Section/Launch Your Membership In Minutes.png`,
    },
  ],
};

const membershipLaunchSection: IdeaToIncomeProps = {
  eyebrow: "LAUNCH",
  title: "Recurring Revenue. Global Members. Zero Friction.",
  subtitle:
    "Pricing plans, renewals, and member management — all automated so your membership scales without overhead.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  columns: 3,
  cards: [
    {
      title: "Flexible Subscription Plans",
      description:
        "Monthly, quarterly, or annual — set pricing tiers that match your audience and upgrade paths.",
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Launch Section/Flexible Pricing Models.png`,
    },
    {
      title: "Global Members, Local Pricing",
      description:
        "Accept payments in 120+ currencies and adapt pricing per country automatically.",
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Launch Section/Sell Worldwide, Get Paid Locally.png`,
    },
    {
      title: "Renewals On Autopilot",
      description:
        "Automatic renewals, reminders, and invoice delivery keep recurring revenue flowing.",
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Launch Section/Taxes and Invoices, Automated.png`,
    },
  ],
};

const membershipSellSection: AutomationsProps = {
  eyebrow: "SELL",
  title: "Convert Fans Into Members\nWith Pages That Sell",
  subtitle:
    "Publish membership landing pages that explain the value, handle payments, and onboard members — all in minutes.",
  imagePosition: "left",
  features: [
    {
      id: "membership-pages",
      title: "Conversion-Ready Membership Pages",
      description:
        "Beautiful, modern pages showing tiers, benefits, and testimonials — built by AI, editable by you.",
      bullets: [
        { icon: "window", text: "AI-designed tier comparison tables" },
        { icon: "lightbulb", text: "AI-generated copy and benefit bullets" },
        { icon: "share", text: "Mobile-first responsive layouts" },
        { icon: "question", text: "Add reviews, FAQs, and social proof" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Sell Section/Create Conversion Ready Landing Pages.png`,
    },
    {
      id: "membership-payments",
      title: "Accept Payments Seamlessly",
      description:
        "Make buying effortless with secure payments, global gateways, and flexible pricing options.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "tag", text: "Built-in checkout with one-click purchase" },
        { icon: "globe", text: "Support for global cards, UPI, wallets & more" },
        { icon: "receipt", text: "One-time, subscription, EMI & payment plans" },
        { icon: "moon", text: "Automated invoices, tax handling & receipts" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Sell Section/Accept Payments Seamlessly.png`,
    },
    {
      id: "membership-marketing",
      title: "Capture Leads & Automate Marketing",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "envelope", text: "Lead capture forms and funnel-ready pages" },
        { icon: "share", text: "Email broadcasts & drip campaigns" },
        { icon: "window", text: "Automated follow-ups for abandoned carts" },
        { icon: "flask", text: "Push notifications on mobile apps" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Sell Section/Capture Leads & Automate Marketing.png`,
    },
    {
      id: "membership-insights",
      title: "Track Sales, Revenue & Engagement",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "chart", text: "Revenue by product & source" },
        { icon: "graduation", text: "Engagement and completion tracking" },
        { icon: "flag", text: "Marketing campaign performance" },
        { icon: "lightbulb", text: "Learner performance report" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Membership Page/Sell Section/Track Sales, Revenue & Engagement.png`,
    },
  ],
};

const membershipScaleSection: AiTeamProps = homeV2Data.aiTeam;

const membershipStoriesSection: ProductV2StoriesSection = {
  eyebrow: "STORIES",
  titleMuted: "Creators Building Memberships",
  titleBright: "That Compound",
  subtitle:
    "Thousands of creators use Graphy to turn one-off sales into predictable recurring revenue — with happier, stickier fans.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  stories: sharedProductStories,
};

const membershipFaqSection: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question: "Can I have different monthly and yearly pricing?",
      answer:
        "Yes — set distinct prices for monthly, quarterly, and annual plans, and offer discounts on longer commitments.",
    },
    {
      question: "Can I interact exclusively with my members?",
      answer:
        "Yes, dedicated members-only channels are created automatically so you can talk to paying members directly.",
    },
    {
      question: "Are memberships one-time or recurring?",
      answer:
        "Memberships on Graphy are recurring subscriptions by default, with optional one-time lifetime tiers.",
    },
    {
      question: "Is there a cap on the number of paid members?",
      answer:
        "No — Graphy supports unlimited paid members with no additional per-seat fees.",
    },
    {
      question: "Can I configure reminders for renewals?",
      answer:
        "Yes, Graphy ships with built-in automations that send renewal reminders to members — fully customizable.",
    },
    {
      question: "Can I mix free and paid memberships?",
      answer:
        "Absolutely. Use free tiers for top-of-funnel discovery and paid tiers for premium content.",
    },
  ],
};

// ---------------------------------------------------------------------------
// DIGITAL PRODUCTS
// ---------------------------------------------------------------------------

const digitalProductsSeo: GraphyHeadPropType = {
  title: "Create & Sell Digital Products → Graphy",
  description:
    "Create and sell digital products like ebooks, templates, and downloads with payments, delivery, and analytics built in.",
  canonicalPath: "sell-digital-products",
  countryCode: "",
};

const digitalProductsCreateSection: AutomationsProps = {
  eyebrow: "CREATE",
  title: "Create Digital Products\nFast, Flexible & Brand-Ready",
  subtitle:
    "Turn your ideas into polished digital products. Start with your content — Graphy packages it into a professional, instantly deliverable format without technical complexity.",
  features: [
    {
      id: "multi-formats",
      title: "Choose Your Digital Product Format",
      description:
        "Decide what you want to sell. Build professional digital products without worrying about hosting, delivery, or file limits.",
      bullets: [
        { icon: "window", text: "Ebooks, guides & workbooks" },
        { icon: "clipboard", text: "Templates, planners & checklists" },
        { icon: "headset", text: "Videos and images" },
        { icon: "share", text: "Texts and links" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Create Section/Choose Your Digital Product Format.png`,
    },
    {
      id: "streamlined-payments",
      title: "Package & Organize Your Product Content",
      description:
        "Package your knowledge into useful, organized digital products your audience can put to work immediately.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "tag", text: "Add usage instructions, examples & previews" },
        { icon: "globe", text: "Organize files inside structured folders" },
        { icon: "receipt", text: "Add bonus materials & extra resources" },
        { icon: "moon", text: "Limit access to your digital products" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Create Section/Package & Organize Your Product Content.png`,
    },
    {
      id: "audience-control",
      title: "Design A Seamless Buyer Experience",
      description:
        "Present your digital products clearly, beautifully, and in a way that maximizes conversions.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "envelope", text: "Auto-generate product pages with AI" },
        { icon: "share", text: "Add images, descriptions & benefit lists" },
        { icon: "clipboard", text: "Add testimonials, FAQs & bonus sections" },
        { icon: "chart", text: "Set one-time pricing" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Create Section/Design A Seamless Buyer Experience.png`,
    },
    {
      id: "automated-comms",
      title: "Launch Your Digital Store In Minutes",
      description:
        "Go from upload to publish-ready instantly. Everything is mobile-ready, SEO-ready, and optimized for sales.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "envelope", text: "Publish instantly with one click" },
        { icon: "flag", text: "Generate product description with AI" },
        { icon: "flask", text: "Enable global payments immediately" },
        { icon: "lightbulb", text: "Preview your store across devices" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Create Section/Launch Your Digital Store In Minutes.png`,
    },
  ],
};

const digitalProductsLaunchSection: IdeaToIncomeProps = {
  eyebrow: "LAUNCH",
  title: "Sell Globally.\nPayments Built for Modern Creators.",
  subtitle:
    "Complex payments kill conversions. Graphy gives you flexible pricing, global and local gateways, and automated taxes and invoices.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  columns: 3,
  cards: [
    {
      title: "Flexible Pricing Models",
      description:
        "Sell one-time courses, subscriptions, bundles, or payment plans — all from one place.",
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Launch Section/Flexible Pricing Models.png`,
    },
    {
      title: "Sell Worldwide, Get Paid Locally",
      description:
        "Accept payments in 120+ currencies with global and local gateways.",
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Launch Section/Sell Worldwide, Get Paid Locally.png`,
    },
    {
      title: "Taxes and Invoices, Automated",
      description:
        "Taxes and invoices are handled automatically — no spreadsheets needed.",
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Launch Section/Taxes and Invoices, Automated.png`,
    },
  ],
};

const digitalProductsSellSection: AutomationsProps = {
  eyebrow: "SELL",
  title: "Sell & Market\nFrom One Unified Dashboard",
  subtitle:
    "Turn your knowledge into structured, engaging courses. Start with an idea. Graphy helps you turn it into a professional course experience without technical complexity.",
  imagePosition: "left",
  features: [
    {
      id: "dp-pages",
      title: "Create Conversion Ready Landing Pages",
      description:
        "Turn visitors into buyers with pages designed for clarity, trust, and action — no design skills required.",
      bullets: [
        { icon: "window", text: "Conversion-optimized sales pages made ready by AI Website builder" },
        { icon: "lightbulb", text: "AI-generated copy, layouts & CTAs" },
        { icon: "share", text: "Mobile-responsive designs ready to publish" },
        { icon: "question", text: "Add reviews, proof, FAQs & bonuses easily" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Sell Section/Create Conversion Ready Landing Pages.png`,
    },
    {
      id: "dp-payments",
      title: "Accept Payments Seamlessly",
      description:
        "Make buying effortless with secure payments, global gateways, and flexible pricing options.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "tag", text: "Built-in checkout with one-click purchase" },
        { icon: "globe", text: "Support for global cards, UPI, wallets & more" },
        { icon: "receipt", text: "One-time, subscription, EMI & payment plans" },
        { icon: "moon", text: "Automated invoices, tax handling & receipts" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Sell Section/Accept Payments Seamlessly.png`,
    },
    {
      id: "dp-marketing",
      title: "Capture Leads & Automate Marketing",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "envelope", text: "Lead capture forms and funnel-ready pages" },
        { icon: "share", text: "Email broadcasts & drip campaigns" },
        { icon: "window", text: "Automated follow-ups for abandoned carts" },
        { icon: "flask", text: "Push notifications on mobile apps" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Sell Section/Capture Leads & Automate Marketing.png`,
    },
    {
      id: "dp-insights",
      title: "Track Sales, Revenue & Engagement",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "chart", text: "Revenue by product & source" },
        { icon: "graduation", text: "Engagement and completion tracking" },
        { icon: "flag", text: "Marketing campaign performance" },
        { icon: "lightbulb", text: "Learner performance report" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Sell Section/Track Sales, Revenue & Engagement.png`,
    },
  ],
};

const digitalProductsScaleSection: AiTeamProps = homeV2Data.aiTeam;

const digitalProductsStoriesSection: ProductV2StoriesSection = {
  eyebrow: "STORIES",
  titleMuted: "Creators Who Sell More,",
  titleBright: "Without Doing More",
  subtitle:
    "Thousands of creators use Graphy to automate selling, deliver better experiences, and grow their course businesses globally.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  stories: sharedProductStories,
};

const digitalProductsFaqSection: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question: "Can I sell a digital product for free?",
      answer: "Yes, you can sell a digital product of your choice for free",
    },
    {
      question: "Can I upload a zip file and sell?",
      answer: "Yes, zip files are supported in digital products",
    },
    {
      question: "Can I sell links on Graphy?",
      answer:
        "Yes, you can select link as a product type on digital products",
    },
    {
      question:
        "Is there a limit on the total number of digital products I can sell?",
      answer:
        "No there is no limit on total number of digital products you can sell",
    },
    {
      question:
        "Is there a size limit on the files I can upload in digital products?",
      answer:
        "Yes, you can only upload upto 200MBs. If you want to sell a file of greater size, you can host it on a server of your choice and mention the link to it",
    },
  ],
};

// ---------------------------------------------------------------------------
// TELEGRAM COMMUNITY
// ---------------------------------------------------------------------------

const telegramCommunitySeo: GraphyHeadPropType = {
  title: "Build, Manage & Monetize Your Community → Graphy",
  description:
    "Launch a branded community, monetize it with paid Community Clubs, or bundle it free with any course, membership, or product you sell.",
  canonicalPath: "community-chat-for-creators",
  countryCode: "",
};

const telegramCommunityCreateSection: AutomationsProps = {
  eyebrow: "BUILD",
  title: "Build a Community\nPeople Actually Show Up To",
  subtitle:
    "Give your audience a home base to connect, ask questions, and stay engaged. Free to start, yours to shape.",
  features: [
    {
      id: "enable-your-community",
      title: "Set Up Channels in Minutes",
      description:
        "Create dedicated spaces for every topic your audience cares about.",
      bullets: [
        { icon: "window", text: "Create unlimited public or private channels" },
        { icon: "key", text: "Restrict any channel to members only" },
        { icon: "question", text: "Rename or reorganize channels anytime" },
        { icon: "share", text: "Name and describe each channel for easy navigation" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Create Section/Enable Your Community.png`,
    },
    {
      id: "content-conversation-spaces",
      title: "Create Content & Conversation Spaces",
      description:
        "Give members a place to post, reply, and learn from each other.",
      bullets: [
        { icon: "clipboard", text: "Share posts with photos, links & updates" },
        { icon: "envelope", text: "Let members reply and start threads" },
        { icon: "lightbulb", text: "Turn everyday questions into peer-to-peer learning" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Create Section/Create Content & Conversation Spaces.png`,
    },
    {
      id: "run-amas",
      title: "Run AMAs",
      description:
        "Host Ask-Me-Anything sessions where members post questions and you answer them.",
      bullets: [
        { icon: "lightbulb", text: "Members post their questions in a dedicated AMA channel" },
        { icon: "share", text: "Answer questions directly in the thread" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Create Section/Build Events That Bring Members Together.png`,
    },
    {
      id: "manage-with-ease",
      title: "Manage Your Community With Ease",
      description:
        "Moderate content and control access, all from one place.",
      bullets: [
        { icon: "key", text: "Set posting & access permissions per channel" },
        { icon: "moon", text: "Moderate posts and comments with one click" },
        { icon: "window", text: "Pin important announcements to the top" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Create Section/Manage Your Community With Ease.png`,
    },
  ],
};

const telegramCommunityLaunchSection: IdeaToIncomeProps = {
  eyebrow: "MONETIZE",
  title: "Monetise Your Community",
  subtitle:
    "Launch a standalone Community Club, price it your way, and get paid with no extra setup.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  columns: 3,
  cards: [
    {
      title: "Choose Your Pricing Plan",
      description:
        "Offer free access, a one-time fee, or a subscription. You decide.",
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Lauch Section/Flexible Pricing Models.png`,
    },
    {
      title: "Get Paid in Any Currency",
      description:
        "Charge in dollars, euros, pounds, yen, or rupees. Graphy handles the conversion.",
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Lauch Section/Sell Worldwide, Get Paid Locally.png`,
    },
    {
      title: "Add Multiple Pricing Plans",
      description:
        "Creators can add as many pricing plans as they like, each with its own price and status.",
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Lauch Section/Taxes and Invoices, Automated.png`,
    },
  ],
};

const telegramCommunitySellSection: AutomationsProps = {
  eyebrow: "COMMUNITY CLUBS",
  title: "Launch a Paid Community Club\nin Minutes",
  subtitle:
    "From idea to a monetized, branded space, in three steps.",
  imagePosition: "left",
  features: [
    {
      id: "tc-pages",
      title: "Give Your Club a Landing Page That Converts",
      description:
        "Set your Club's name, description, and cover image, then add an FAQ that turns visitors into members.",
      bullets: [
        { icon: "window", text: "Name your Club and describe what it's about" },
        { icon: "lightbulb", text: "Add a cover image that matches your brand" },
        { icon: "question", text: "Answer common questions with a built-in FAQ" },
        { icon: "share", text: "Changes go live instantly, no rebuild needed" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Sell Section/Create Conversion Ready Landing Pages.png`,
    },
    {
      id: "tc-payments",
      title: "Accept Payments Seamlessly",
      description:
        "Let members pay however they want, all built in.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "tag", text: "Built-in checkout with one-click purchase" },
        { icon: "globe", text: "Support for cards, UPI, netbanking & more" },
        { icon: "clipboard", text: "One-time payments & subscriptions" },
        { icon: "receipt", text: "Custom button text and transaction fee controls" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Sell Section/Accept Payments Seamlessly.png`,
    },
    {
      id: "tc-insights",
      title: "Track Sales, Revenue & Engagement",
      description:
        "See exactly how your Club is performing, all in one dashboard.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "chart", text: "Revenue and new signups at a glance" },
        { icon: "graduation", text: "Member and engagement tracking" },
        { icon: "flag", text: "Search and filter members by plan or date" },
        { icon: "lightbulb", text: "Independent status per pricing plan" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Community Page/Sell Section/Track Sales, Revenue & Engagement.png`,
    },
  ],
};

const telegramCommunityScaleSection: AiTeamProps = homeV2Data.aiTeam;

const telegramCommunityStoriesSection: ProductV2StoriesSection = {
  eyebrow: "STORIES",
  titleMuted: "Creators Who Sell More,",
  titleBright: "Without Doing More",
  subtitle:
    "Thousands of creators use Graphy to automate selling, deliver better experiences, and grow their course businesses globally.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  stories: sharedProductStories,
};

const telegramCommunityFaqSection: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question:
        "What's the difference between a Community Club and a Product Club?",
      answer:
        "A Product Club is created automatically and included free with anything you sell, access is tied to that purchase. A Community Club is a standalone space you create and price on its own, independent of any product.",
    },
    {
      question: "How much can I charge for a Community Club?",
      answer:
        "You set your own price: a one-time fee with optional discount pricing, or a subscription with your own billing cycle.",
    },
    {
      question:
        "Will creating a Community Club affect my existing Product Club or Open Community?",
      answer:
        "No, all three can run side by side, each with its own membership and status.",
    },
    {
      question: "Can I edit a Club after it's published?",
      answer:
        "Yes, update the landing page, channels, or pricing anytime. Most changes go live instantly and existing members aren't disrupted.",
    },
    {
      question:
        "Will the audience be notified of what's happening in the community?",
      answer:
        "Yes, automated notifications are configured for all primary events, including new posts, replies, and payment reminders.",
    },
    {
      question: "Is community available on Android and iOS apps too?",
      answer:
        "Yes, every Community and Club feature is available to your audience on Android and iOS apps.",
    },
  ],
};

// ---------------------------------------------------------------------------
// AI AVATAR
// ---------------------------------------------------------------------------

const aiavatarSeo: GraphyHeadPropType = {
  title: "Create Your Own AI Avatar → Graphy",
  description:
    "Create an AI avatar that teaches and supports your audience 24/7—powered by your own content.",
  canonicalPath: "ai-avatar",
  countryCode: "",
};

const aiavatarCreateSection: AutomationsProps = {
  eyebrow: "CREATE",
  title: "Launch Your AI Avatar\nIn Under 10 Minutes",
  subtitle:
    "Turn your expertise into 24/7 tutoring with an AI Avatar that sounds like you, teaches like you, and earns while you sleep.",
  features: [
    {
      id: "add-knowledge",
      title: "Add Your Knowledge",
      description:
        "Upload your social media links, course notes, lessons, and FAQs — the Avatar learns from your content.",
      bullets: [
        { icon: "clipboard", text: "Import notes, lessons, and FAQs" },
        { icon: "share", text: "Connect social channels and blog feeds" },
        { icon: "window", text: "Upload PDFs, videos, and audio" },
        { icon: "key", text: "Private, secure knowledge vault" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Create Section/Train Your Avatar On Your Content.png`,
    },
    {
      id: "train-avatar",
      title: "Train Your AI Avatar",
      description:
        "We make it sound like you — with your tone, your phrasing, and your teaching style.",
      bullets: [
        { icon: "lightbulb", text: "Voice, tone, and style calibration" },
        { icon: "flask", text: "Test conversations before launch" },
        { icon: "flag", text: "Set boundaries on what it can say" },
        { icon: "graduation", text: "Continuously improves as you add content" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Create Section/Clone Your Voice, Tone & Personality.png`,
    },
    {
      id: "launch-dashboard",
      title: "Launch on Your Dashboard",
      description:
        "Your Avatar appears inside the learner dashboard and answers course questions 24/7 — in every language.",
      bullets: [
        { icon: "headset", text: "Available in every learner dashboard" },
        { icon: "globe", text: "Multi-language conversations out of the box" },
        { icon: "moon", text: "Answers questions while you sleep" },
        { icon: "envelope", text: "Escalates tough questions to you" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Create Section/Launch Your AI Avatar In Minutes.png`,
    },
    {
      id: "monetize",
      title: "Monetize Access",
      description:
        "Set pricing and start earning from every interaction — one-time, subscription, or usage-based plans.",
      bullets: [
        { icon: "tag", text: "One-time, subscription, or usage pricing" },
        { icon: "receipt", text: "Automated invoices and taxes" },
        { icon: "chart", text: "Revenue per conversation tracking" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Create Section/Set Rules, Permissions & Boundaries for Your Avatar.png`,
    },
  ],
};

const aiavatarLaunchSection: IdeaToIncomeProps = {
  eyebrow: "LAUNCH",
  title: "Your Knowledge, Available 24/7. Priced Your Way.",
  subtitle:
    "Launch a digital version of you that tutors, coaches, and sells — without adding hours to your day.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  columns: 3,
  cards: [
    {
      title: "Flexible Pricing Plans",
      description:
        "Offer your AI Avatar as a free resource, one-time purchase, or recurring subscription — and change your model anytime.",
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Launch Section/Flexible Pricing Models.png`,
    },
    {
      title: "Country-Specific Pricing",
      description:
        "Set different prices for different markets and let buyers pay in their local currency — no manual conversions needed.",
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Launch Section/Sell Worldwide, Get Paid Locally.png`,
    },
    {
      title: "Easy Checkout",
      description:
        "Buyers go from click to access in seconds with a streamlined checkout that works on any device, anywhere.",
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Launch Section/Taxes and Invoices, Automated.png`,
    },
  ],
};

const aiavatarSellSection: AutomationsProps = {
  eyebrow: "SELL",
  title: "Turn Every Interaction\nInto Revenue",
  subtitle:
    "Sell access to your AI Avatar with flexible pricing, beautiful landing pages, and analytics that tell you what's working.",
  imagePosition: "left",
  features: [
    {
      id: "aia-pages",
      title: "Conversion-Ready Avatar Pages",
      description:
        "Showcase your AI Avatar with pages that explain the value, demo the experience, and convert visitors into paying users.",
      bullets: [
        { icon: "window", text: "AI-designed Avatar landing pages" },
        { icon: "lightbulb", text: "AI-generated copy and benefits" },
        { icon: "share", text: "Mobile-first responsive layouts" },
        { icon: "question", text: "Add reviews, demos, and FAQs" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Sell Section/Create Conversion Ready Landing Pages.png`,
    },
    {
      id: "aia-payments",
      title: "Accept Payments Seamlessly",
      description:
        "Make buying effortless with secure payments, global gateways, and flexible pricing options.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "tag", text: "Built-in checkout with one-click purchase" },
        { icon: "globe", text: "Support for global cards, UPI, wallets & more" },
        { icon: "receipt", text: "One-time, subscription, EMI & payment plans" },
        { icon: "moon", text: "Automated invoices, tax handling & receipts" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Sell Section/Accept Payments Seamlessly.png`,
    },
    {
      id: "aia-marketing",
      title: "Capture Leads & Automate Marketing",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "envelope", text: "Lead capture forms and funnel-ready pages" },
        { icon: "share", text: "Email broadcasts & drip campaigns" },
        { icon: "window", text: "Automated follow-ups for abandoned carts" },
        { icon: "flask", text: "Push notifications on mobile apps" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Sell Section/Capture Leads & Automate Marketing.png`,
    },
    {
      id: "aia-insights",
      title: "Track Sales, Revenue & Engagement",
      description:
        "See exactly what's working — and what's not — with insights that help you optimize for growth.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "chart", text: "Revenue by product & source" },
        { icon: "graduation", text: "Engagement and completion tracking" },
        { icon: "flag", text: "Marketing campaign performance" },
        { icon: "lightbulb", text: "Learner performance report" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Sell Section/Track Sales, Revenue & Engagement.png`,
    },
  ],
};

const aiavatarScaleSection: AiTeamProps = homeV2Data.aiTeam;

const aiavatarStoriesSection: ProductV2StoriesSection = {
  eyebrow: "STORIES",
  titleMuted: "Creators Multiplying Their Impact",
  titleBright: "With AI Avatars",
  subtitle:
    "From always-on tutors to 1:1 coaching at scale, creators use AI Avatar to grow completions, engagement, and recurring revenue.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  stories: sharedProductStories,
};

const aiavatarFaqSection: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question: "How accurate is my AI Avatar?",
      answer:
        "Your Avatar learns directly from your content, so its answers reflect your knowledge, tone, and teaching style.",
    },
    {
      question: "Can I change the responses of my AI Avatar?",
      answer:
        "Yes — update your knowledge base anytime and your Avatar adapts automatically to the latest content.",
    },
    {
      question: "How can I monetize my AI Avatar?",
      answer:
        "Charge for access as a one-time plan, a subscription, or per-conversation. Many creators bundle Avatar access as a premium tier.",
    },
    {
      question: "Is the Avatar customizable?",
      answer:
        "Absolutely — you control tone, scope of questions, and what knowledge it can access. Retrain anytime.",
    },
    {
      question: "Does it support multiple languages?",
      answer:
        "Yes, your Avatar can answer in every language your learners speak, without any extra configuration.",
    },
    {
      question: "How long does it take to launch?",
      answer:
        "Most creators are live in under 10 minutes — upload your content, pick a tone, and publish.",
    },
  ],
};

// ---------------------------------------------------------------------------
// COACHING
// ---------------------------------------------------------------------------

const coachingSeo: GraphyHeadPropType = {
  title: "Launch & Sell Coaching Programs → Graphy",
  description:
    "Create coaching programs, automate session bookings, manage your calendar, and deliver professional 1:1 coaching experiences — all from one dashboard.",
  canonicalPath: "sell-coaching-programs",
  countryCode: "",
};

const coachingCreateSection: AutomationsProps = {
  eyebrow: "CREATE",
  title: "Create Coaching Programs\nWithout the Admin Headache",
  subtitle:
    "Build structured programs in minutes. Define sessions, set your availability, and go live — Graphy handles the logistics end-to-end.",
  features: [
    {
      id: "design-program",
      title: "Design Your Coaching Program",
      description:
        "Launch a professional coaching program in three simple steps — no technical setup required.",
      bullets: [
        { icon: "clipboard", text: "Name your program, add description & cover image" },
        { icon: "flag", text: "Structure multi-session programs with agendas & notes" },
        { icon: "window", text: "Build your public coaching landing page with FAQs & cover" },
        { icon: "graduation", text: "Set pricing: free, one-time payment, or subscription" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Create/Design Your Coaching Program.png`,
    },
    {
      id: "setup-sessions",
      title: "Set Up Sessions & Agendas",
      description:
        "Build structured sessions your clients look forward to — with clear agendas, coach notes, and shareable resources.",
      bulletsHeading: "What You Can Configure:",
      bullets: [
        { icon: "clipboard", text: "Add session name, duration & detailed agenda" },
        { icon: "lightbulb", text: "Attach resources shared with learners before sessions" },
        { icon: "key", text: "Add private coach notes (not visible to learners)" },
        { icon: "graduation", text: "Mark sessions complete and track progress per client" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Create/Sessions & Agendas.png`,
    },
    {
      id: "smart-scheduling",
      title: "Set Your Availability & Scheduling Rules",
      description:
        "Define when you're available, sync with Google Calendar, and let Graphy automatically surface the right slots to clients.",
      bulletsHeading: "What It Handles:",
      bullets: [
        { icon: "window", text: "Set weekly availability with day-by-day time slots" },
        { icon: "share", text: "Sync with Google Calendar to block busy times automatically" },
        { icon: "question", text: "Configure min. booking notice, buffer & cancellation windows" },
        { icon: "chart", text: "Cap reschedule limits per session to protect your schedule" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Create/Set Your Availability.png`,
    },
    {
      id: "launch-program",
      title: "Go Live in Minutes",
      description:
        "From wizard to published coaching program — everything is mobile-ready and designed for conversions out of the box.",
      bulletsHeading: "What You Can Do:",
      bullets: [
        { icon: "window", text: "Publish your program landing page with one click" },
        { icon: "envelope", text: "Set up automated booking confirmations & reminders" },
        { icon: "tag", text: "Enable payments and publish instantly" },
        { icon: "flag", text: "Preview your program on mobile & desktop" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Create/Go Live.png`,
    },
  ],
};

const coachingLaunchSection: IdeaToIncomeProps = {
  eyebrow: "LAUNCH",
  title: "Bookings, Renewals & Payments — All On Autopilot",
  subtitle:
    "Focus on coaching. Graphy automates every booking, renewal, and payment lifecycle event — from first booking to final session.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  columns: 3,
  cards: [
    {
      title: "Smart Slot Booking",
      description:
        "Learners pick from your real-time available slots — synced with your Google Calendar and existing bookings, so double-booking is impossible.",
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Launch/Slot Booking.png`,
    },
    {
      title: "Effortless Renewals",
      description:
        "Set subscription plans with weekly, monthly, or yearly billing cycles. Enable auto-pay so clients stay enrolled without manual action each cycle.",
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Launch/Renewals.png`,
    },
    {
      title: "Flexible Pricing Plans",
      description:
        "Offer free access, charge a one-time fee, or set up recurring subscriptions — all configured in a simple three-step pricing wizard.",
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Launch/Flexible Pricing.png`,
    },
  ],
};

// Coach-facing framing of the learner experience: each feature is written
// as something YOU give your learners. Eyebrow "DELIVER" completes the
// CREATE → LAUNCH → DELIVER narrative arc — the coach builds the program,
// launches it, then delivers a polished experience to learners.
const coachingDeliverSection: AutomationsProps = {
  eyebrow: "DELIVER",
  title: "Give Every Learner\nA Seamless Experience",
  subtitle:
    "Offer your learners a polished, friction-free journey — from browsing your program to booking a slot, joining live, and reviewing the replay — so they stay engaged and make real progress.",
  imagePosition: "left",
  features: [
    {
      id: "browse-enroll",
      title: "Make Program Browsing Effortless",
      description:
        "Give learners a clear, well-organised view of your coaching programs and exactly what's included — so they can explore everything with ease before they commit, on any device.",
      bullets: [
        { icon: "clipboard", text: "Program detail page showcasing sessions, agenda & your coach profile" },
        { icon: "window", text: "Session-by-session breakdown so learners know what they're getting" },
        { icon: "tag", text: "Pricing and inclusions laid out clearly upfront" },
        { icon: "globe", text: "Fully mobile-responsive — learners browse from anywhere" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Deliver/Enroll Instantly.png`,
    },
    {
      id: "book-sessions",
      title: "Offer Easy Scheduling & Rescheduling",
      description:
        "Give learners a real-time slot picker synced with your calendar — and let them reschedule within the limits you set. No back-and-forth, no double-booking.",
      bulletsHeading: "What You Offer:",
      bullets: [
        { icon: "window", text: "Calendar view of available slots based on your availability" },
        { icon: "share", text: "Self-serve rescheduling within the reschedule limits you set" },
        { icon: "key", text: "Timezone-aware — learners see times in their local zone" },
        { icon: "flag", text: "Instant confirmation + updated Google Calendar invite for both of you" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Deliver/Schedule Or Reschedule.png`,
    },
    {
      id: "join-watch",
      title: "Deliver Live Sessions & On-Demand Replays",
      description:
        "Let learners join live with one click, then give them everything afterwards — recording, AI summary, and full transcript, all in one place.",
      bulletsHeading: "What You Give Learners After Each Session:",
      bullets: [
        { icon: "headset", text: "Join button activates 5 minutes before session start" },
        { icon: "chart", text: "Session recording available to watch & download" },
        { icon: "lightbulb", text: "AI-generated session summary and action items" },
        { icon: "clipboard", text: "Full speaker-labelled transcript with timestamps" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Deliver/Watch Replays.png`,
    },
    {
      id: "track-manage",
      title: "Keep Learners On Track Automatically",
      description:
        "Make sure your learners always know what's next — with automated reminders, a clear progress view, and self-serve reschedule or cancel options.",
      bulletsHeading: "What You Give Every Learner:",
      bullets: [
        { icon: "envelope", text: "Automated email reminders before every session" },
        { icon: "graduation", text: "Progress tracker showing sessions completed vs. remaining" },
        { icon: "share", text: "Self-serve reschedule & cancel from their own dashboard" },
        { icon: "moon", text: "All sessions and recordings in one organised view" },
      ],
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/Deliver/Track Progress.png`,
    },
  ],
};

const coachingScaleSection: AiTeamProps = {
  ...homeV2Data.aiTeam,
  titleMuted: "Your AI Avatar.",
  titleBright: "Your Extended Team.",
  subtitle:
    "Graphy creates an AI Avatar for you, trained on your content.\nIt interacts with your audience and answers their questions when you are not available.",
  cards: [
    {
      kind: "ai-operator",
      badge: "Gets smarter over time",
      title: "Trained on Your Content",
      description:
        "Your AI avatar learns from your session recordings and transcripts to speak in your voice and style.",
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/AIsection/AI Operator.png`,
    },
    {
      kind: "ai-agents",
      badge: "24x7 presence",
      title: "Always Available",
      description:
        "When you're offline, your AI avatar steps in to answer questions, guide learners, and keep them engaged.",
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/AIsection/AI Agents.png`,
    },
    {
      kind: "ai-avatar",
      badge: "Monetize 1:1 interactions",
      title: "Personal at Scale",
      description:
        "Every learner gets a 1:1 interaction that feels like it came from you — without you being in the room.",
      image: `${getCDNURL()}graphy-web/V4/images/Product Pages/Sell Coaching/AIsection/AI Avatar.png`,
    },
  ],
};

const coachingStoriesSection: ProductV2StoriesSection = {
  eyebrow: "STORIES",
  titleMuted: "Coaches Scaling Their Impact",
  titleBright: "With Less Admin",
  subtitle:
    "Thousands of coaches use Graphy to automate bookings, deliver professional sessions, and grow their coaching businesses without extra overhead.",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  stories: sharedProductStories,
};

const coachingFaqSection: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question: "What happens if a learner misses a session?",
      answer:
        "Missed sessions appear in the learner's dashboard with a 'Missed' status. As long as their plan hasn't expired, they can rebook directly from their session list — the same slot-picker flow applies and they pick a new available time.",
    },
    {
      question: "How does the slot booking work for learners?",
      answer:
        "Learners pick from your real-time available slots in an interactive slot picker. Slots are automatically computed from your defined availability, Google Calendar busy events, and already-booked sessions — so double-booking is never possible.",
    },
    {
      question: "Can I sync my Google Calendar with Graphy coaching?",
      answer:
        "Yes. Connect your Google Calendar and Graphy automatically blocks off any busy slots when showing available times to learners. New bookings also create calendar invites for both parties.",
    },
    {
      question: "What pricing models are available for coaching programs?",
      answer:
        "Graphy supports three pricing types for coaching programs: Free, One-time payment, and Subscription. For subscriptions you can set a billing cycle (weekly, monthly, or yearly) and enable auto-pay. You can also set a custom plan duration (limited or unlimited) and customise the checkout button text.",
    },
    {
      question: "Are reminders sent automatically before sessions?",
      answer:
        "Yes — Graphy sends automated email reminders at 24 hours, 1 hour, and 15 minutes before each scheduled session, to both the coach and the learner. You can customize the schedule and content from the Reminders & Notifications panel.",
    },
    {
      question: "Can learners reschedule or cancel sessions on their own?",
      answer:
        "Yes. Learners can request a reschedule or cancel a session from their dashboard. You control the rules — set a maximum number of reschedules per session, a minimum reschedule notice, and a cancellation window from the Scheduling Rules settings.",
    },
    {
      question: "Do learners get session recordings and transcripts?",
      answer:
        "Yes. Once a session is marked complete, learners can watch the recording, download it, and review an AI-generated summary and a full speaker-labelled transcript from their dashboard. Recordings stay available for a set window — and once it passes, a download link is emailed so they keep a copy.",
    },
  ],
};

/** Keyed record — add more entries as other product slugs get their V2
 *  data. Only slugs present in this record render the V2 layout. */
export const productV2Data: Partial<Record<ProductPageSlugEnum, ProductV2DataType>> = {
  [ProductPageSlugEnum.courses]: {
    seoData: coursesSeo,
    hero: {
      eyebrow: "Online Courses",
      eyebrowIcon: faBookOpen,
      // Golden yellow — measured from graphy.com/create-and-sell-course-online.
      // Inter Medium 500, 18px, icon color matches text.
      eyebrowColorClass: "text-[#FFC415]",
      titleMuted: "Create Online Courses",
      titleBright: "With Ease",
      description:
        "Graphy is an all-in-one platform for coaches, creators, and educators to create online courses, price them flexibly, sell globally, and scale with AI Agent.",
      stats: [
        { value: "200,000+", label: "Creators Worldwide" },
        { value: "₹1,500 Crore+", label: "Creator Revenue" },
        { value: "5 Million+", label: "Learners Enrolled" },
        { value: "150+", label: "Countries Served" },
      ],
      ctas: [heroPrimaryCta, heroSecondaryCta],
      heroImage: `${getCDNURL()}graphy-web/V4/images/Courses Page/Hero Section/Hero section image.png`,
    },
    sections: [
      { kind: "accordion", data: coursesCreateSection },
      { kind: "card-grid", data: coursesLaunchSection },
      { kind: "accordion", data: coursesSellSection },
      { kind: "ai-team", data: coursesScaleSection },
      { kind: "stories", data: coursesStoriesSection },
      { kind: "faq", data: coursesFaqSection },
    ],
    // Reuse the exact CTA banner + footer from the homepage so the chrome
    // is identical across surfaces. No duplication of copy or links.
    ctaBanner: homeV2Data.ctaBanner,
    footer: homeV2Data.footer,
  },
  [ProductPageSlugEnum.webinar]: {
    seoData: webinarSeo,
    hero: {
      eyebrow: "Live Webinars",
      eyebrowIcon: faVideo,
      eyebrowColorClass: "text-[#5766F2]",
      titleMuted: "Host Live Webinars",
      titleBright: "That Convert",
      description:
        "Run paid or free live webinars with integrated payments, reminders, and recordings. Graphy handles the logistics so you can focus on teaching.",
      stats: [
        { value: "55,000+", label: "Webinars Hosted" },
        { value: "₹1,500 Crore+", label: "Creator Revenue" },
        { value: "5 Million+", label: "Learners Enrolled" },
        { value: "150+", label: "Countries Served" },
      ],
      ctas: [heroPrimaryCta, heroSecondaryCta],
      heroImage: `${getCDNURL()}graphy-web/V4/images/Webinar Page/Hero Section/Webinar home 1.png`,
    },
    sections: [
      { kind: "accordion", data: webinarCreateSection },
      { kind: "card-grid", data: webinarLaunchSection },
      { kind: "accordion", data: webinarSellSection },
      { kind: "ai-team", data: webinarScaleSection },
      { kind: "stories", data: webinarStoriesSection },
      { kind: "faq", data: webinarFaqSection },
    ],
    ctaBanner: homeV2Data.ctaBanner,
    footer: homeV2Data.footer,
  },
  [ProductPageSlugEnum.membership]: {
    seoData: membershipSeo,
    hero: {
      eyebrow: "Memberships",
      eyebrowIcon: faUsers,
      eyebrowColorClass: "text-[#CA26A5]",
      titleMuted: "Launch a Members-Only Club",
      titleBright: "With Recurring Revenue",
      description:
        "Ignite your fanbase and unlock predictable monthly revenue. Graphy gives you everything to build, run, and scale a premium membership.",
      stats: [
        { value: "10,000+", label: "Members Enrolled" },
        { value: "₹1,500 Crore+", label: "Creator Revenue" },
        { value: "200,000+", label: "Creators Worldwide" },
        { value: "150+", label: "Countries Served" },
      ],
      ctas: [heroPrimaryCta, heroSecondaryCta],
      heroImage: `${getCDNURL()}graphy-web/V4/images/Membership Page/Hero Section/Group 1116611903.png`,
    },
    sections: [
      { kind: "accordion", data: membershipCreateSection },
      { kind: "card-grid", data: membershipLaunchSection },
      { kind: "accordion", data: membershipSellSection },
      { kind: "ai-team", data: membershipScaleSection },
      { kind: "stories", data: membershipStoriesSection },
      { kind: "faq", data: membershipFaqSection },
    ],
    ctaBanner: homeV2Data.ctaBanner,
    footer: homeV2Data.footer,
  },
  [ProductPageSlugEnum.digitalProducts]: {
    seoData: digitalProductsSeo,
    hero: {
      eyebrow: "Digital Products",
      eyebrowIcon: faBoxOpen,
      eyebrowColorClass: "text-[#4F7F06]",
      titleMuted: "Create, Sell & Scale",
      titleBright: "Your Digital Products",
      description:
        "Graphy helps you package your knowledge into high-value digital products and sell them globally with automated delivery, secure hosting, and built-in marketing.",
      stats: [
        { value: "200,000+", label: "Creators Worldwide" },
        { value: "₹1,500 Crore+", label: "Creator Revenue" },
        { value: "5 Million+", label: "Learners Enrolled" },
        { value: "150+", label: "Countries Served" },
      ],
      ctas: [heroPrimaryCta, heroSecondaryCta],
      heroImage: `${getCDNURL()}graphy-web/V4/images/Digital Products Page/Hero Section/image 502.png`,
    },
    sections: [
      { kind: "accordion", data: digitalProductsCreateSection },
      { kind: "card-grid", data: digitalProductsLaunchSection },
      { kind: "accordion", data: digitalProductsSellSection },
      { kind: "ai-team", data: digitalProductsScaleSection },
      { kind: "stories", data: digitalProductsStoriesSection },
      { kind: "faq", data: digitalProductsFaqSection },
    ],
    ctaBanner: homeV2Data.ctaBanner,
    footer: homeV2Data.footer,
  },
  [ProductPageSlugEnum.telegramCommunity]: {
    seoData: telegramCommunitySeo,
    hero: {
      eyebrow: "Community",
      eyebrowIcon: faUsers,
      eyebrowColorClass: "text-[#007F8C]",
      titleMuted: "Turn Your Community Into",
      titleBright: "a Revenue Stream",
      description:
        "Every course, membership, or product you sell already comes with its own free community. Now you can also launch a standalone Community Club and charge for access on its own.",
      stats: [
        { value: "200,000+", label: "Creators Worldwide" },
        { value: "₹1,500 Crore+", label: "Creator Revenue" },
        { value: "5 Million+", label: "Learners Enrolled" },
        { value: "150+", label: "Countries Served" },
      ],
      ctas: [heroPrimaryCta, heroSecondaryCta],
      heroImage: `${getCDNURL()}graphy-web/V4/images/Community Page/Hero Section/community home 1.png`,
    },
    sections: [
      { kind: "accordion", data: telegramCommunityCreateSection },
      { kind: "card-grid", data: telegramCommunityLaunchSection },
      { kind: "accordion", data: telegramCommunitySellSection },
      { kind: "ai-team", data: telegramCommunityScaleSection },
      { kind: "stories", data: telegramCommunityStoriesSection },
      { kind: "faq", data: telegramCommunityFaqSection },
    ],
    ctaBanner: homeV2Data.ctaBanner,
    footer: homeV2Data.footer,
  },
  [ProductPageSlugEnum.aiavatar]: {
    seoData: aiavatarSeo,
    hero: {
      eyebrow: "AI Avatar",
      eyebrowIcon: faWandMagicSparkles,
      eyebrowColorClass: "text-[#FF6A3D]",
      titleMuted: "Launch Your AI Avatar",
      titleBright: "& Monetize Your Mind",
      description:
        "Turn your expertise into a 24/7 AI tutor that sounds like you, teaches like you, and earns for you — while you focus on creating.",
      stats: [
        { value: "200,000+", label: "Happy Creators" },
        { value: "₹1,500 Crore+", label: "Creator Revenue" },
        { value: "5 Million+", label: "Learners Assisted" },
        { value: "24/7", label: "Always On" },
      ],
      ctas: [heroPrimaryCta, heroSecondaryCta],
      heroImage: `${getCDNURL()}graphy-web/V4/images/AI Avatar Page/Hero Section/AI Avatar Home 1.png`,
    },
    sections: [
      { kind: "accordion", data: aiavatarCreateSection },
      { kind: "card-grid", data: aiavatarLaunchSection },
      { kind: "accordion", data: aiavatarSellSection },
      { kind: "ai-team", data: aiavatarScaleSection },
      { kind: "stories", data: aiavatarStoriesSection },
      { kind: "faq", data: aiavatarFaqSection },
    ],
    ctaBanner: homeV2Data.ctaBanner,
    footer: homeV2Data.footer,
  },
  [ProductPageSlugEnum.coaching]: {
    seoData: coachingSeo,
    hero: {
      eyebrow: "Coaching",
      eyebrowIcon: faCalendarCheck,
      eyebrowColorClass: "text-[#7C3AED]",
      titleMuted: "Launch & Sell Coaching Programs",
      titleBright: "With Smart Scheduling",
      description:
        "Graphy's coaching platform lets you create structured programs, automate session bookings, manage your calendar, and deliver professional 1:1 coaching experiences — all from one dashboard.",
      stats: [
        { value: "200,000+", label: "Creators Worldwide" },
        { value: "₹1,500 Crore+", label: "Creator Revenue" },
        { value: "5 Million+", label: "Learners Enrolled" },
        { value: "150+", label: "Countries Served" },
      ],
      ctas: [heroPrimaryCta, heroSecondaryCta],
      heroImage: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/Coaching.png`,
    },
    sections: [
      { kind: "accordion", data: coachingCreateSection },
      { kind: "card-grid", data: coachingLaunchSection },
      { kind: "accordion", data: coachingDeliverSection },
      { kind: "ai-team", data: coachingScaleSection },
      { kind: "stories", data: coachingStoriesSection },
      { kind: "faq", data: coachingFaqSection },
    ],
    ctaBanner: homeV2Data.ctaBanner,
    footer: homeV2Data.footer,
  },
};
